import React, { useEffect, useState, useCallback } from 'react';
import { getTrackingStats, getTrackingLogs, getTrackingUsers, getTrackingUser, getTrackingSessions } from '../services/api';

const TABS = [
  { key: 'dashboard', label: 'Vue d\'ensemble', icon: '\u{1F4CA}' },
  { key: 'logs', label: 'Journal d\'audit', icon: '\u{1F4CB}' },
  { key: 'users', label: 'Activite utilisateurs', icon: '\u{1F465}' },
  { key: 'sessions', label: 'Sessions', icon: '\u{1F511}' },
];

const ACTION_COLORS: Record<string, string> = {
  LOGIN: '#2196F3', VIEW: '#607D8B', CREATE: '#4CAF50', UPDATE: '#FF9800',
  DELETE: '#f44336', EXPORT: '#9C27B0', SEARCH: '#00BCD4', SEND_SMS: '#E91E63',
  RESET_PASSWORD: '#FF5722', TOGGLE_STATUS: '#795548', LOGOUT: '#9E9E9E'
};

const ACTION_LABELS: Record<string, string> = {
  LOGIN: 'Connexion', VIEW: 'Consultation', CREATE: 'Creation', UPDATE: 'Modification',
  DELETE: 'Suppression', EXPORT: 'Export', SEARCH: 'Recherche', SEND_SMS: 'Envoi SMS',
  RESET_PASSWORD: 'Reset MDP', TOGGLE_STATUS: 'Activation/Desact.', LOGOUT: 'Deconnexion'
};

const MODULE_LABELS: Record<string, string> = {
  auth: 'Authentification', users: 'Utilisateurs', dashboard: 'Tableau de bord',
  partenaires: 'Partenaires', forages: 'Forages', abonnes: 'Abonnes',
  factures: 'Factures', paiements: 'Paiements', productions: 'Productions',
  secteurs: 'Secteurs', localites: 'Localites', charges: 'Charges',
  comptabilite: 'Comptabilite', analytics: 'Analytics', 'mobile-payments': 'Mobile Money',
  'sms-broadcast': 'SMS Broadcast', statistiques: 'Statistiques', tracking: 'Tracking'
};

function formatDate(d: string) {
  if (!d) return '-';
  const dt = new Date(d);
  return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function timeAgo(d: string) {
  if (!d) return '-';
  const now = new Date().getTime();
  const then = new Date(d).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return `il y a ${diff}s`;
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}

const Badge: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <span style={{
    display: 'inline-block', padding: '2px 10px', borderRadius: 12,
    background: color + '18', color, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap'
  }}>{text}</span>
);

const StatusDot: React.FC<{ status: string }> = ({ status }) => (
  <span style={{
    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
    background: status === 'success' ? '#4CAF50' : '#f44336', marginRight: 6
  }} />
);

// Mini bar chart for hourly distribution
const HourChart: React.FC<{ data: any[] }> = ({ data }) => {
  if (!data || !data.length) return null;
  const max = Math.max(...data.map((d: any) => d.total), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 80 }}>
      {Array.from({ length: 24 }, (_, h) => {
        const item = data.find((d: any) => d.heure === h);
        const val = item ? item.total : 0;
        const height = (val / max) * 70;
        return (
          <div key={h} title={`${h}h: ${val} actions`} style={{
            flex: 1, minWidth: 8, background: val > 0 ? '#1a73e8' : '#eee',
            height: Math.max(height, 2), borderRadius: '3px 3px 0 0', cursor: 'pointer',
            opacity: val > 0 ? 0.7 + (val / max) * 0.3 : 0.3
          }} />
        );
      })}
    </div>
  );
};

// Activity timeline chart (last 30 days)
const ActivityChart: React.FC<{ data: any[] }> = ({ data }) => {
  if (!data || !data.length) return null;
  const max = Math.max(...data.map((d: any) => d.total), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 100, padding: '10px 0' }}>
      {data.map((d: any, i: number) => {
        const height = (d.total / max) * 85;
        const jour = new Date(d.jour);
        const label = jour.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <span style={{ fontSize: 9, color: '#999' }}>{d.total}</span>
            <div title={`${label}: ${d.total} actions, ${d.utilisateurs} utilisateurs`} style={{
              width: '100%', minWidth: 6, background: `linear-gradient(180deg, #1a73e8, #42a5f5)`,
              height: Math.max(height, 3), borderRadius: '3px 3px 0 0', cursor: 'pointer'
            }} />
            {i % 5 === 0 && <span style={{ fontSize: 8, color: '#aaa', whiteSpace: 'nowrap' }}>{label}</span>}
          </div>
        );
      })}
    </div>
  );
};

const Tracking: React.FC = () => {
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [logs, setLogs] = useState<any>({ data: [], total: 0, page: 1, totalPages: 0 });
  const [usersActivity, setUsersActivity] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any>({ data: [], total: 0, page: 1, totalPages: 0 });
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userDetail, setUserDetail] = useState<any>(null);

  // Filters
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [logPage, setLogPage] = useState(1);
  const [sessPage, setSessPage] = useState(1);
  const [expandedLog, setExpandedLog] = useState<number | null>(null);

  const buildParams = useCallback(() => {
    const p: Record<string, string> = {};
    if (dateDebut) p.date_debut = dateDebut;
    if (dateFin) p.date_fin = dateFin;
    return p;
  }, [dateDebut, dateFin]);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTrackingStats(buildParams());
      setStats(res.data);
    } catch { }
    setLoading(false);
  }, [buildParams]);

  const loadLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const p: Record<string, string> = { ...buildParams(), page: String(page), limit: '40' };
      if (filterAction) p.action = filterAction;
      if (filterModule) p.module = filterModule;
      if (filterStatus) p.status = filterStatus;
      if (searchTerm) p.search = searchTerm;
      const res = await getTrackingLogs(p);
      setLogs(res.data);
      setLogPage(page);
    } catch { }
    setLoading(false);
  }, [buildParams, filterAction, filterModule, filterStatus, searchTerm]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTrackingUsers(buildParams());
      setUsersActivity(res.data);
    } catch { }
    setLoading(false);
  }, [buildParams]);

  const loadSessions = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await getTrackingSessions({ page: String(page), limit: '40' });
      setSessions(res.data);
      setSessPage(page);
    } catch { }
    setLoading(false);
  }, []);

  const loadUserDetail = useCallback(async (userId: string) => {
    try {
      const res = await getTrackingUser(userId, { limit: '30' });
      setUserDetail(res.data);
    } catch { }
  }, []);

  useEffect(() => {
    if (tab === 'dashboard') loadStats();
    if (tab === 'logs') loadLogs(1);
    if (tab === 'users') loadUsers();
    if (tab === 'sessions') loadSessions(1);
  }, [tab, loadStats, loadLogs, loadUsers, loadSessions]);

  useEffect(() => {
    if (tab === 'logs') loadLogs(1);
  }, [filterAction, filterModule, filterStatus, searchTerm, loadLogs, tab]);

  useEffect(() => {
    if (selectedUser) loadUserDetail(selectedUser);
  }, [selectedUser, loadUserDetail]);

  const s = stats;

  // =============== RENDER TABS ===============

  const renderDashboard = () => {
    if (!s) return <p style={{ color: '#999', textAlign: 'center', padding: 40 }}>Chargement...</p>;
    const r = s.resume;

    return (
      <div>
        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total actions', value: r.total_actions, color: '#1a73e8', icon: '\u{1F4C8}' },
            { label: 'Utilisateurs actifs', value: r.utilisateurs_actifs, color: '#4CAF50', icon: '\u{1F465}' },
            { label: 'Connexions', value: r.connexions, color: '#2196F3', icon: '\u{1F511}' },
            { label: 'Connexions echouees', value: r.connexions_echouees, color: '#f44336', icon: '\u{26A0}\u{FE0F}' },
            { label: 'Creations', value: r.creations, color: '#4CAF50', icon: '\u{2795}' },
            { label: 'Modifications', value: r.modifications, color: '#FF9800', icon: '\u{270F}\u{FE0F}' },
            { label: 'SMS envoyes', value: r.sms_envoyes, color: '#E91E63', icon: '\u{1F4E8}' },
            { label: 'Erreurs', value: r.erreurs, color: '#f44336', icon: '\u{274C}' },
          ].map((kpi, i) => (
            <div key={i} style={{
              background: '#fff', borderRadius: 12, padding: '18px 20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderLeft: `4px solid ${kpi.color}`
            }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{kpi.icon} {kpi.label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: kpi.color }}>{(kpi.value || 0).toLocaleString('fr-FR')}</div>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#444' }}>Activite (30 derniers jours)</h3>
            <ActivityChart data={s.parJour} />
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#444' }}>Distribution horaire</h3>
            <HourChart data={s.parHeure} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#aaa', marginTop: 4 }}>
              <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
            </div>
          </div>
        </div>

        {/* Module & Users Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          {/* Actions par module */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 14, color: '#444' }}>Actions par module</h3>
            {s.parModule?.map((m: any, i: number) => {
              const maxVal = s.parModule[0]?.total || 1;
              return (
                <div key={i} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                    <span style={{ fontWeight: 500 }}>{MODULE_LABELS[m.module] || m.module}</span>
                    <span style={{ color: '#666' }}>{m.total} {m.erreurs > 0 && <span style={{ color: '#f44336' }}>({m.erreurs} err)</span>}</span>
                  </div>
                  <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3 }}>
                    <div style={{ height: 6, width: `${(m.total / maxVal) * 100}%`, background: '#1a73e8', borderRadius: 3 }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Top utilisateurs actifs */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 14, color: '#444' }}>Top utilisateurs actifs</h3>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee' }}>
                  <th style={{ textAlign: 'left', padding: 6 }}>Utilisateur</th>
                  <th style={{ textAlign: 'center', padding: 6 }}>Actions</th>
                  <th style={{ textAlign: 'center', padding: 6 }}>Connexions</th>
                  <th style={{ textAlign: 'right', padding: 6 }}>Derniere act.</th>
                </tr>
              </thead>
              <tbody>
                {s.topUsers?.map((u: any, i: number) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}
                      onClick={() => { setSelectedUser(u.user_id); setTab('users'); }}>
                    <td style={{ padding: '6px' }}>
                      <div style={{ fontWeight: 500 }}>{u.user_prenom} {u.user_nom}</div>
                      <div style={{ fontSize: 10, color: '#999' }}>{u.user_email}</div>
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 600, color: '#1a73e8' }}>{u.total_actions}</td>
                    <td style={{ textAlign: 'center' }}>{u.connexions}</td>
                    <td style={{ textAlign: 'right', fontSize: 10, color: '#888' }}>{timeAgo(u.derniere_action)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions critiques recentes */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 14, color: '#444' }}>{'\u{1F6A8}'} Actions critiques recentes</h3>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #eee' }}>
                <th style={{ textAlign: 'left', padding: 6 }}>Date</th>
                <th style={{ textAlign: 'left', padding: 6 }}>Utilisateur</th>
                <th style={{ textAlign: 'center', padding: 6 }}>Action</th>
                <th style={{ textAlign: 'left', padding: 6 }}>Module</th>
                <th style={{ textAlign: 'left', padding: 6 }}>Description</th>
                <th style={{ textAlign: 'center', padding: 6 }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {s.critiques?.map((c: any, i: number) => (
                <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: 6, fontSize: 11, whiteSpace: 'nowrap' }}>{formatDate(c.created_at)}</td>
                  <td style={{ padding: 6 }}>{c.user_prenom} {c.user_nom}</td>
                  <td style={{ padding: 6, textAlign: 'center' }}>
                    <Badge text={ACTION_LABELS[c.action] || c.action} color={ACTION_COLORS[c.action] || '#666'} />
                  </td>
                  <td style={{ padding: 6 }}>{MODULE_LABELS[c.module] || c.module}</td>
                  <td style={{ padding: 6, fontSize: 11, color: '#666', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.description}</td>
                  <td style={{ padding: 6, textAlign: 'center' }}><StatusDot status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderLogs = () => (
    <div>
      {/* Filters */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16, padding: 14,
        background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
      }}>
        <input type="text" placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, minWidth: 180 }} />
        <select value={filterAction} onChange={e => setFilterAction(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}>
          <option value="">Toutes les actions</option>
          {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filterModule} onChange={e => setFilterModule(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}>
          <option value="">Tous les modules</option>
          {Object.entries(MODULE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}>
          <option value="">Tous les statuts</option>
          <option value="success">Succes</option>
          <option value="error">Erreur</option>
        </select>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#888', alignSelf: 'center' }}>
          {logs.total} resultats
        </span>
      </div>

      {/* Logs table */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
              <th style={{ textAlign: 'left', padding: '10px 8px' }}>Date</th>
              <th style={{ textAlign: 'left', padding: '10px 8px' }}>Utilisateur</th>
              <th style={{ textAlign: 'center', padding: '10px 8px' }}>Action</th>
              <th style={{ textAlign: 'left', padding: '10px 8px' }}>Module</th>
              <th style={{ textAlign: 'left', padding: '10px 8px' }}>Description</th>
              <th style={{ textAlign: 'center', padding: '10px 8px' }}>IP</th>
              <th style={{ textAlign: 'center', padding: '10px 8px' }}>Statut</th>
            </tr>
          </thead>
          <tbody>
            {logs.data?.map((log: any) => (
              <React.Fragment key={log.id}>
                <tr style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer', background: expandedLog === log.id ? '#f8f9ff' : 'transparent' }}
                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}>
                  <td style={{ padding: '8px', fontSize: 11, whiteSpace: 'nowrap' }}>{formatDate(log.created_at)}</td>
                  <td style={{ padding: '8px' }}>
                    <div style={{ fontWeight: 500, fontSize: 12 }}>{log.user_prenom} {log.user_nom}</div>
                    <div style={{ fontSize: 10, color: '#999' }}>{log.user_email}</div>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <Badge text={ACTION_LABELS[log.action] || log.action} color={ACTION_COLORS[log.action] || '#666'} />
                  </td>
                  <td style={{ padding: '8px', fontSize: 12 }}>{MODULE_LABELS[log.module] || log.module}</td>
                  <td style={{ padding: '8px', fontSize: 11, color: '#555', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.description}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', fontSize: 10, color: '#888', fontFamily: 'monospace' }}>{log.ip_address}</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}><StatusDot status={log.status} /></td>
                </tr>
                {expandedLog === log.id && (
                  <tr>
                    <td colSpan={7} style={{ background: '#f8f9ff', padding: '12px 20px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 12 }}>
                        <div>
                          <strong>ID Ressource:</strong> {log.resource_id || '-'}<br />
                          <strong>Role:</strong> {log.user_role || '-'}<br />
                          <strong>Partenaire ID:</strong> {log.partenaire_id || '-'}
                        </div>
                        <div>
                          {log.old_values && (
                            <div><strong>Anciennes valeurs:</strong>
                              <pre style={{ background: '#fff', padding: 8, borderRadius: 6, fontSize: 11, maxHeight: 100, overflow: 'auto' }}>
                                {JSON.stringify(typeof log.old_values === 'string' ? JSON.parse(log.old_values) : log.old_values, null, 2)}
                              </pre>
                            </div>
                          )}
                          {log.new_values && (
                            <div><strong>Nouvelles valeurs:</strong>
                              <pre style={{ background: '#fff', padding: 8, borderRadius: 6, fontSize: 11, maxHeight: 100, overflow: 'auto' }}>
                                {JSON.stringify(typeof log.new_values === 'string' ? JSON.parse(log.new_values) : log.new_values, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {(!logs.data || logs.data.length === 0) && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: '#999' }}>Aucun log d'audit trouve</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {logs.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button onClick={() => loadLogs(logPage - 1)} disabled={logPage <= 1}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 12 }}>
            Precedent
          </button>
          <span style={{ alignSelf: 'center', fontSize: 12, color: '#666' }}>
            Page {logPage} / {logs.totalPages}
          </span>
          <button onClick={() => loadLogs(logPage + 1)} disabled={logPage >= logs.totalPages}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 12 }}>
            Suivant
          </button>
        </div>
      )}
    </div>
  );

  const renderUsers = () => (
    <div>
      {selectedUser && userDetail ? (
        <div>
          <button onClick={() => { setSelectedUser(null); setUserDetail(null); }}
            style={{ marginBottom: 16, padding: '6px 16px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
            \u2190 Retour a la liste
          </button>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>
              {userDetail.user?.user_prenom} {userDetail.user?.user_nom}
              <Badge text={userDetail.user?.user_role || ''} color="#1a73e8" />
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: '#666' }}>
              {userDetail.user?.user_email} | {userDetail.total} actions au total |
              Premiere activite: {formatDate(userDetail.user?.premiere_action)} |
              Derniere: {formatDate(userDetail.user?.derniere_action)}
            </p>
          </div>
          {/* User timeline */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 14 }}>Historique des actions</h3>
            <div style={{ position: 'relative', paddingLeft: 24 }}>
              {userDetail.data?.map((log: any, i: number) => (
                <div key={i} style={{ position: 'relative', paddingBottom: 16, borderLeft: '2px solid #e0e0e0', paddingLeft: 20, marginLeft: 6 }}>
                  <div style={{
                    position: 'absolute', left: -7, top: 3, width: 12, height: 12, borderRadius: '50%',
                    background: log.status === 'success' ? (ACTION_COLORS[log.action] || '#666') : '#f44336', border: '2px solid #fff'
                  }} />
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>{formatDate(log.created_at)}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Badge text={ACTION_LABELS[log.action] || log.action} color={ACTION_COLORS[log.action] || '#666'} />
                    <span style={{ fontSize: 12, color: '#444' }}>{MODULE_LABELS[log.module] || log.module}</span>
                    <StatusDot status={log.status} />
                  </div>
                  {log.description && <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>{log.description}</div>}
                  {log.ip_address && <div style={{ fontSize: 10, color: '#aaa', fontFamily: 'monospace' }}>IP: {log.ip_address}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
                <th style={{ textAlign: 'left', padding: '10px 8px' }}>Utilisateur</th>
                <th style={{ textAlign: 'center', padding: '10px 8px' }}>Role</th>
                <th style={{ textAlign: 'center', padding: '10px 8px' }}>Actions</th>
                <th style={{ textAlign: 'center', padding: '10px 8px' }}>Connexions</th>
                <th style={{ textAlign: 'center', padding: '10px 8px' }}>Creations</th>
                <th style={{ textAlign: 'center', padding: '10px 8px' }}>Modifs</th>
                <th style={{ textAlign: 'center', padding: '10px 8px' }}>Erreurs</th>
                <th style={{ textAlign: 'center', padding: '10px 8px' }}>Jours actifs</th>
                <th style={{ textAlign: 'center', padding: '10px 8px' }}>IPs</th>
                <th style={{ textAlign: 'right', padding: '10px 8px' }}>Derniere act.</th>
              </tr>
            </thead>
            <tbody>
              {usersActivity.map((u: any, i: number) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                    onClick={() => setSelectedUser(u.user_id)}>
                  <td style={{ padding: 8 }}>
                    <div style={{ fontWeight: 500 }}>{u.user_prenom} {u.user_nom}</div>
                    <div style={{ fontSize: 10, color: '#999' }}>{u.user_email}</div>
                  </td>
                  <td style={{ textAlign: 'center', padding: 8 }}>
                    <Badge text={u.user_role || '-'} color="#1a73e8" />
                  </td>
                  <td style={{ textAlign: 'center', padding: 8, fontWeight: 600, color: '#1a73e8' }}>{u.total_actions}</td>
                  <td style={{ textAlign: 'center', padding: 8 }}>{u.connexions}</td>
                  <td style={{ textAlign: 'center', padding: 8 }}>{u.creations}</td>
                  <td style={{ textAlign: 'center', padding: 8 }}>{u.modifications}</td>
                  <td style={{ textAlign: 'center', padding: 8, color: u.erreurs > 0 ? '#f44336' : '#999' }}>{u.erreurs}</td>
                  <td style={{ textAlign: 'center', padding: 8 }}>{u.jours_actifs}</td>
                  <td style={{ textAlign: 'center', padding: 8 }}>{u.ips_distinctes}</td>
                  <td style={{ textAlign: 'right', padding: 8, fontSize: 11, color: '#888' }}>{timeAgo(u.derniere_action)}</td>
                </tr>
              ))}
              {usersActivity.length === 0 && (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 30, color: '#999' }}>Aucune activite enregistree</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderSessions = () => (
    <div>
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
              <th style={{ textAlign: 'left', padding: '10px 8px' }}>Date</th>
              <th style={{ textAlign: 'left', padding: '10px 8px' }}>Utilisateur</th>
              <th style={{ textAlign: 'center', padding: '10px 8px' }}>Role</th>
              <th style={{ textAlign: 'center', padding: '10px 8px' }}>Statut</th>
              <th style={{ textAlign: 'left', padding: '10px 8px' }}>Description</th>
              <th style={{ textAlign: 'center', padding: '10px 8px' }}>IP</th>
              <th style={{ textAlign: 'left', padding: '10px 8px' }}>Navigateur</th>
            </tr>
          </thead>
          <tbody>
            {sessions.data?.map((s: any, i: number) => {
              // Parse user agent for readable browser info
              const ua = s.user_agent || '';
              let browser = 'Inconnu';
              if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
              else if (ua.includes('Firefox')) browser = 'Firefox';
              else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
              else if (ua.includes('Edg')) browser = 'Edge';
              let os = '';
              if (ua.includes('Windows')) os = 'Windows';
              else if (ua.includes('Mac OS')) os = 'macOS';
              else if (ua.includes('Linux')) os = 'Linux';
              else if (ua.includes('Android')) os = 'Android';
              else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

              return (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: s.status === 'error' ? '#fff5f5' : 'transparent' }}>
                  <td style={{ padding: 8, fontSize: 11, whiteSpace: 'nowrap' }}>{formatDate(s.created_at)}</td>
                  <td style={{ padding: 8 }}>
                    <div style={{ fontWeight: 500 }}>{s.user_prenom} {s.user_nom}</div>
                    <div style={{ fontSize: 10, color: '#999' }}>{s.user_email}</div>
                  </td>
                  <td style={{ textAlign: 'center', padding: 8 }}>
                    <Badge text={s.user_role || '-'} color="#1a73e8" />
                  </td>
                  <td style={{ textAlign: 'center', padding: 8 }}>
                    <Badge text={s.status === 'success' ? 'Succes' : 'Echec'} color={s.status === 'success' ? '#4CAF50' : '#f44336'} />
                  </td>
                  <td style={{ padding: 8, fontSize: 11, color: '#666' }}>{s.description}</td>
                  <td style={{ padding: 8, textAlign: 'center', fontSize: 10, fontFamily: 'monospace', color: '#888' }}>{s.ip_address}</td>
                  <td style={{ padding: 8, fontSize: 11, color: '#666' }}>
                    {browser}{os ? ` / ${os}` : ''}
                  </td>
                </tr>
              );
            })}
            {(!sessions.data || sessions.data.length === 0) && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: '#999' }}>Aucune session enregistree</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {sessions.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button onClick={() => loadSessions(sessPage - 1)} disabled={sessPage <= 1}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 12 }}>
            Precedent
          </button>
          <span style={{ alignSelf: 'center', fontSize: 12, color: '#666' }}>
            Page {sessPage} / {sessions.totalPages}
          </span>
          <button onClick={() => loadSessions(sessPage + 1)} disabled={sessPage >= sessions.totalPages}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 12 }}>
            Suivant
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* Compact toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        background: '#fff', borderRadius: 10, padding: '8px 14px', marginBottom: 18,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)'
      }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '7px 16px', borderRadius: 8, border: 'none', fontSize: 13,
            background: tab === t.key ? '#1a73e8' : 'transparent',
            color: tab === t.key ? '#fff' : '#555',
            fontWeight: tab === t.key ? 600 : 400, cursor: 'pointer', whiteSpace: 'nowrap',
            transition: 'all 0.2s'
          }}>
            {t.icon} {t.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)}
            style={{ padding: '5px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12 }} />
          <span style={{ color: '#999', fontSize: 12 }}>a</span>
          <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)}
            style={{ padding: '5px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12 }} />
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 10, color: '#999', fontSize: 13 }}>Chargement...</div>}

      {tab === 'dashboard' && renderDashboard()}
      {tab === 'logs' && renderLogs()}
      {tab === 'users' && renderUsers()}
      {tab === 'sessions' && renderSessions()}
    </div>
  );
};

export default Tracking;
